'use client';

import CircleButton from '@components/circle-button/CircleButton';
import Compass from '@components/compass/Compass';
import { RESERVED_FLATS_31832 } from '@lib/flags';
import { flatsLocalStorageSelector, removeLocalFlats, setLocalFlats } from '@lib/store/slices/FavoriteFlatSlice';
import { setFlatImg, setFlatLoading } from '@lib/store/slices/FlatSlice';
import { getFlatPin } from '@lib/tools/_getFlatPin';
import { pushDataLayer } from '@lib/tools/pushDataLayer';
import classNames from 'classnames';
import NextImage from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';
import FlatConfigureBtns from '../flat-configure/FlatConfigureBtns';
import FlatFloorsScheme from '../flat-floors-scheme/FlatFloorsScheme';
import { GenplanSignatures } from '../genplan-signatures/GenplanSignatures';
import WindowViews from '../window-views/WindowViews';
import style from './FlatInfoDetails.module.scss';

const FlatInfoDetails = ({
  flat,
  cdnFloorSvg,
  cdnFloorPng,
  cdnFurnished,
  cdnDimension,
  pathToGenplan,
  pathToGenplanSvg,
}) => {
  const dispatch = useDispatch();
  const flatfavorites = useSelector(flatsLocalStorageSelector);
  const flatInfo = useSelector(state => state?.flatInfo?.flatDisplays);
  const genPlan = useSelector(state => state?.flatInfo?.flatGenPlan);
  const bulk = useSelector(state => state?.flatInfo?.flatInfo?.bulk?.id);

  const [isActive, setIsActive] = useState(false);

  const furnishedSrc = cdnFurnished;
  const dimensionSrc = cdnDimension;
  const floorSvgSrc = cdnFloorSvg;
  const floorPngSrc = cdnFloorPng;

  const activeButton = flatInfo?.flatContent?.active || 'withFurniture';
  const isReservedFlat = flat.status === 'reserve' && RESERVED_FLATS_31832;

  useEffect(() => {
    if (!flat) return;

    dispatch(
      setFlatLoading({
        id: 1,
        type: 'withFurniture',
        image: furnishedSrc,
      }),
    );

    dispatch(
      setFlatImg([
        { id: 1, image: furnishedSrc, characteristic: 'withFurniture' },
        { id: 2, image: dimensionSrc, characteristic: 'withSize' },
        { id: 3, image: floorSvgSrc, imagePng: floorPngSrc, characteristic: 'onFloor' },
        { id: 4, image: pathToGenplan, characteristic: 'onGenplan' },
      ]),
    );
  }, [dispatch, flat, furnishedSrc, dimensionSrc, floorSvgSrc, floorPngSrc, pathToGenplan]);

  useEffect(() => {
    const isFavorite = flatfavorites?.some(f => f?.id === flat?.id);
    setIsActive(!!isFavorite);
  }, [flatfavorites, flat?.id]);

  const handleLocalStorage = () => {
    if (isActive) {
      dispatch(removeLocalFlats(flat));
    } else {
      dispatch(setLocalFlats(flat));
      pushDataLayer('click_success_favorites');
    }
    setIsActive(prev => !prev);
  };

  const beforeSvgInjection = useCallback(() => {
    const k1 = document.querySelector('#bulk1');
    const k21 = document.querySelector('#bulk2_1');
    const k22 = document.querySelector('#bulk2_2');
    const k23 = document.querySelector('#bulk2_3');

    if (!k1 || !k21) return;

    const k1pin = '_1';
    const k21pin = '_2_1';
    const k22pin = '_2_2';
    const k23pin = '_2_3';

    if (flat?.bulk?.id === 9335) {
      k1.classList.add(style.activeHouse);
      const pin = document.querySelector(`#${getFlatPin(flat.bulk.id, Number(flat.number)) + k1pin}`);
      if (pin) pin.style.opacity = '1';
    } else if (flat?.bulk?.id === 9758) {
      k21.classList.add(style.activeHouse);
      const pin = document.querySelector(`#${getFlatPin(flat.bulk.id, Number(flat.number)) + k21pin}`);
      if (pin) pin.style.opacity = '1';
    } else if (flat?.bulk?.id === 10099) {
      k23.classList.add(style.activeHouse);
      const pin = document.querySelector(`#${getFlatPin(flat.bulk.id, Number(flat.number)) + k23pin}`);
      if (pin) pin.style.opacity = '1';
    } else if (flat?.bulk?.id === 10100) {
      k22.classList.add(style.activeHouse);
      const pin = document.querySelector(`#${getFlatPin(flat.bulk.id, Number(flat.number)) + k22pin}`);
      if (pin) pin.style.opacity = '1';
    }
  }, [flat?.bulk?.id, flat?.number]);

  let currentImageSrc = flatInfo?.flatContent?.image || furnishedSrc;

  if (activeButton === 'withFurniture') {
    currentImageSrc = furnishedSrc;
  } else if (activeButton === 'withSize') {
    currentImageSrc = dimensionSrc;
  }

  return (
    <div className={style.flatLocation}>
      <div className={style.flatLocation__extra}>
        <div
          className={classNames(style.flatLocation__extra__info, {
            [style.flatLocation__extra__infoReserved]: isReservedFlat,
          })}
        >
          <CircleButton
            genPlan={genPlan}
            flatid={flat?.id}
            tooltipText='Скачать PDF'
            someStyles={style.transparentBg}
            handleClick={() => pushDataLayer('click_success_pdf')}
          >
            <p
              className={classNames(style.flatLocation__extra__pdf, {
                [style.flatLocation__extra__pdf__white]: genPlan,
              })}
            >
              PDF
            </p>
          </CircleButton>
          <div className={isReservedFlat ? style.flatLocation__extra_shareBtnWrapper : ''}>
            <CircleButton isActive={false} genPlan={genPlan} share={true} someStyles={style.transparentBg}>
              <svg
                className={classNames(style.flatLocation__extra_svg_one, {
                  [style.whiteSvg]: genPlan,
                })}
                viewBox='0 0 16 13'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M10.0774 0.226488C9.89548 0.272297 9.739 0.381668 9.62647 0.541699L9.52051 0.692379L9.51 1.56833L9.49949 2.44432H7.57134C6.47517 2.44432 5.48608 2.46028 5.27912 2.48134C4.32375 2.57848 3.58086 2.84334 2.80566 3.36327C1.67546 4.12124 0.910215 5.2565 0.602211 6.6321C0.521141 6.99421 0.520508 7.01537 0.520508 9.35825V11.7195L0.627523 11.8719C0.912816 12.2783 1.53072 12.3075 1.81524 11.928C1.86249 11.865 2.3795 10.975 2.96415 9.95018C3.5488 8.92537 4.09706 7.98818 4.18256 7.86756C4.45541 7.48246 4.92907 7.16208 5.43039 7.02353C5.63022 6.96826 5.85174 6.9619 7.56934 6.9619H9.48535L9.50293 7.84351C9.52026 8.71212 9.52181 8.72699 9.6084 8.85482C9.75201 9.06677 9.9337 9.17175 10.1847 9.18771C10.3253 9.19668 10.4449 9.18339 10.5237 9.15009C10.5907 9.12176 11.6991 8.22035 12.9869 7.14693C15.5335 5.02433 15.4795 5.07693 15.4795 4.71724C15.4795 4.54058 15.4098 4.33636 15.3112 4.22407C15.1925 4.08907 10.6434 0.316382 10.5477 0.273632C10.4128 0.213375 10.2101 0.193054 10.0774 0.226488Z'
                  fill={genPlan ? '#FFFFFF' : '#1d1d1d'}
                />
              </svg>
            </CircleButton>
          </div>

          <CircleButton
            handleClick={handleLocalStorage}
            isActive={isActive}
            genPlan={genPlan}
            tooltipText='Добавить в избранное'
            someStyles={style.transparentBg}
            heart={true}
          >
            <svg
              className={classNames(style.flatLocation__extra_svg_two, {
                [style.whiteSvg]: genPlan,
              })}
              viewBox='0 0 16 15'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M3.62193 0.932313C2.11699 1.15627 1.0669 1.88032 0.467674 3.10713C0.0191533 4.02536 -0.126141 5.36992 0.116684 6.35528C0.352189 7.31102 0.916727 8.36296 1.78737 9.46837C2.86195 10.8327 4.52138 12.4145 6.3879 13.8536C6.70427 14.0975 7.83247 14.9179 7.96028 14.997C8.0051 15.0247 8.23372 14.8674 9.06233 14.2389C11.349 12.5044 13.3121 10.6096 14.5125 8.9782C15.6437 7.44102 16.1109 6.15434 15.978 4.94307C15.7221 2.61187 13.8518 0.884854 11.5803 0.882327C10.4135 0.881016 9.34891 1.32788 8.49929 2.17559C8.37026 2.30435 8.20839 2.48261 8.13957 2.57177C8.07076 2.66093 8.00741 2.73388 7.99881 2.73388C7.99021 2.73388 7.92687 2.66093 7.85805 2.57177C7.3161 1.86963 6.4306 1.27256 5.57335 1.03117C5.05229 0.884438 4.22519 0.842543 3.62193 0.932313Z'
                fill={isActive ? '#FFFFFF' : '#1d1d1d'}
              />
            </svg>
          </CircleButton>
        </div>
      </div>

      {genPlan && !flatInfo?.flatContent?.imagePng && (
        <>
          <NextImage
            fill
            id='gen'
            className={style.flatLocation__flatGenPlan}
            src={pathToGenplan}
            alt='Генплан'
            priority
          />
          <SVG id='mask' src={pathToGenplanSvg} className={style.flatLocation__planSvg} onLoad={beforeSvgInjection} />
          <GenplanSignatures />
        </>
      )}

      {!genPlan && !flatInfo?.flatContent?.imagePng && (
        <div className={style.imgWrapper}>
          <NextImage
            fill
            className={style.flatLocation__flat}
            src={currentImageSrc}
            alt='Планировка квартиры'
            priority={activeButton === 'withFurniture'}
            unoptimized={currentImageSrc.endsWith('.svg')}
            id='flatImage'
            itemProp='image'
          />
          <WindowViews flat={flat} />
        </div>
      )}

      {flatInfo?.flatContent?.imagePng && (
        <FlatFloorsScheme
          flatBulk={flat?.bulk_id}
          floorNumber={flat?.floor}
          image={cdnFloorPng}
          svg={cdnFloorSvg}
          flat={flat}
          flatNumber={flat?.number}
        />
      )}

      <div className={style.flatLocation__sub}>
        <div className={style.flatLocation__compasbox}>{flat && <Compass bulk={flat?.bulk_id} />}</div>
        <FlatConfigureBtns active={activeButton || false} bulk={bulk} />
      </div>
    </div>
  );
};

export default FlatInfoDetails;
